/**
 * The case team, and changing it.
 *
 * Two things here are domain rules rather than interface choices.
 *
 * **Adding somebody to the team grants them sight of the matter.** Case team
 * membership is what the `assigned` scope resolves through, so this is an
 * access-control action wearing the clothes of a personnel one. That is why it
 * needs `case.assign` rather than `case.edit`, and why removing the last
 * responsible lawyer is refused by the server with a reason.
 *
 * **Removal closes the row; it never deletes it.** Who handled a matter and
 * when is part of the file, and it is the resolution path for every later
 * question about who could see what. The interface says "Remove" because that
 * is what the user is doing; the record disagrees, correctly.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type CaseDetail,
} from '@legalcrm/api';
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  ConfirmModal,
  EmptyState,
  Form,
  FormActions,
  FormSelect,
  Modal,
  useZodForm,
} from '@legalcrm/ui';
import { useState } from 'react';
import { z } from 'zod';

import { Can } from '@/auth/permissions';
import { humanise } from '@/lib/format';

const ROLE_LABELS: Record<string, string> = {
  responsible_lawyer: 'Responsible lawyer',
  supervising_lawyer: 'Supervising lawyer',
  assistant: 'Assistant',
  paralegal: 'Paralegal',
  observer: 'Observer',
};

/** Deliberately excludes responsible_lawyer: that is a handover, not an addition. */
const ADDABLE_ROLES = [
  { value: 'supervising_lawyer', label: 'Supervising lawyer' },
  { value: 'assistant', label: 'Assistant' },
  { value: 'paralegal', label: 'Paralegal' },
  { value: 'observer', label: 'Observer' },
];

interface Member {
  id: string;
  user: string;
  user_name: string;
  role_on_case: string;
}

interface UserRow {
  id: string;
  user?: string;
  full_name?: string;
  email?: string;
  role_key?: string;
  role_name?: string;
  status?: string;
}

const addSchema = z.object({
  user: z.string().min(1, 'Choose somebody'),
  role_on_case: z.string().min(1, 'Choose their part in the matter'),
});

const handoverSchema = z.object({
  user: z.string().min(1, 'Choose the incoming lawyer'),
  reason: z.string().min(1, 'Say why the matter is moving'),
});

export function CaseTeamCard({ caseRecord }: { caseRecord: CaseDetail }) {
  const api = useApi();
  const [adding, setAdding] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);

  const users = useApiQuery<{ results: UserRow[] } | UserRow[]>(
    qk.list('users'),
    STAFF.users,
  );
  const userRows = Array.isArray(users.data) ? users.data : (users.data?.results ?? []);

  const team = (caseRecord.team ?? []) as unknown as Member[];
  const onTeam = new Set(team.map((member) => member.user));
  const available = userRows.filter(
    (user) => !onTeam.has(String(user.user ?? user.id)) && user.status === 'active',
  );

  const invalidate = [qk.detail('cases', caseRecord.id), qk.all('cases')];

  const add = useApiMutation<unknown, z.infer<typeof addSchema>>({
    mutationFn: (values) => api.post(`${STAFF.caseTeam(caseRecord.id)}/`, values),
    invalidates: invalidate,
  });

  const handover = useApiMutation<unknown, z.infer<typeof handoverSchema>>({
    mutationFn: (values) =>
      api.post(`${STAFF.caseResponsibleLawyer(caseRecord.id)}/`, values),
    invalidates: invalidate,
  });

  const remove = useApiMutation<unknown, string>({
    mutationFn: (assignmentId) =>
      api.delete(`${STAFF.caseTeamMember(caseRecord.id, assignmentId)}/`),
    invalidates: invalidate,
  });

  const addForm = useZodForm(addSchema, {
    defaultValues: { user: '', role_on_case: 'assistant' },
  });
  const handoverForm = useZodForm(handoverSchema, {
    defaultValues: { user: '', reason: '' },
  });

  const userOptions = available.map((user) => ({
    value: String(user.user ?? user.id),
    label: `${user.full_name || user.email} — ${user.role_name ?? humanise(user.role_key ?? '')}`,
  }));

  return (
    <>
      <Card>
        <CardHeader
          title="Team"
          actions={
            <Can module="case" action="assign">
              <div className="flex gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => setHandingOver(true)}>
                  Hand over
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
                  Add
                </Button>
              </div>
            </Can>
          }
        />
        {team.length === 0 ? (
          <EmptyState size="sm" title="Nobody assigned" />
        ) : (
          <ul className="flex flex-col divide-y">
            {team.map((member) => (
              <li key={member.id} className="flex items-center gap-2.5 px-4 py-2.5">
                <Avatar name={member.user_name} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base text-ink">{member.user_name}</p>
                  <p className="truncate text-xs text-ink-muted">
                    {ROLE_LABELS[member.role_on_case] ?? humanise(member.role_on_case)}
                  </p>
                </div>
                {/* The responsible lawyer is changed by handing the matter over, not
                    by removal: a matter must never be left without one, and the
                    server refuses it anyway. */}
                {member.role_on_case !== 'responsible_lawyer' && (
                  <Can module="case" action="assign">
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(member)}>
                      Remove
                    </Button>
                  </Can>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal open={adding} onClose={() => setAdding(false)} title="Add to the team">
        <Form
          form={addForm}
          ariaLabel="Add to the team"
          error={add.error}
          onSubmit={async (values) => {
            await add.mutateAsync(values);
            addForm.reset();
            setAdding(false);
          }}
        >
          <p className="text-sm text-ink-muted">
            Adding somebody to a matter lets them see it. That is the point, and it is
            recorded.
          </p>
          <FormSelect
            form={addForm}
            name="user"
            label="Who"
            placeholder="Choose somebody"
            options={userOptions}
          />
          <FormSelect
            form={addForm}
            name="role_on_case"
            label="Their part in the matter"
            options={ADDABLE_ROLES}
          />
          <FormActions
            submitLabel="Add"
            busy={add.isPending}
            onCancel={() => setAdding(false)}
          />
        </Form>
      </Modal>

      <Modal
        open={handingOver}
        onClose={() => setHandingOver(false)}
        title="Hand the matter over"
      >
        <Form
          form={handoverForm}
          ariaLabel="Hand the matter over"
          error={handover.error}
          onSubmit={async (values) => {
            await handover.mutateAsync(values);
            handoverForm.reset();
            setHandingOver(false);
          }}
        >
          <p className="text-sm text-ink-muted">
            The outgoing lawyer stops being able to see this matter. Their assignment is
            closed rather than erased, so the file still shows they held it.
          </p>
          <FormSelect
            form={handoverForm}
            name="user"
            label="Incoming responsible lawyer"
            placeholder="Choose a lawyer"
            options={userOptions}
          />
          <FormActions
            submitLabel="Hand over"
            busy={handover.isPending}
            onCancel={() => setHandingOver(false)}
          />
        </Form>
      </Modal>

      <ConfirmModal
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.user_name ?? ''}?`}
        confirmLabel="Remove"
        loading={remove.isPending}
        consequences="They will no longer see this matter. The record of their having been on it stays, because who handled a matter and when is part of the file."
        onConfirm={() => {
          if (!removing) return;
          remove
            .mutateAsync(removing.id)
            .then(() => setRemoving(null))
            .catch((error: unknown) => {
              // A guard refusal carries its reason and the modal keeps it on screen.
              // Anything else goes to the shared error surface.
              if (!(error instanceof ConflictError)) throw error;
            });
        }}
      />
    </>
  );
}
